import { useEffect, useMemo, useState } from "react";
import { resolveAgencyLogoUrl } from "@/lib/agencyLogo";
import { propertyHasDisplayableImages, resolvePropertyImageUrls } from "@/lib/propertyMedia";
import {
  cachePropertyBranchId,
  getCachedPropertyBranchId,
  readPropertyBranchId,
} from "@/lib/propertyBranch";
import { mediaAssetsService } from "@/services/mediaAssetsService";
import { propertiesService } from "@/services/propertiesService";
import { useProfileStore } from "@/store/profileStore";
import { useSettingsStore } from "@/store/settingsStore";
import type { PropertySummary } from "@/types/entities";

const MEDIA_PROBE_PAGE_SIZE = 12;

function readHasImagesFromRow(row: PropertySummary): boolean | undefined {
  const raw = row as PropertySummary & {
    hasImages?: boolean | null;
    imageCount?: number | null;
    imagesCount?: number | null;
  };

  if (typeof raw.hasImages === "boolean") return raw.hasImages;
  if (typeof raw.imageCount === "number") return raw.imageCount > 0;
  if (typeof raw.imagesCount === "number") return raw.imagesCount > 0;

  return undefined;
}

async function probePropertyImageUrls(
  row: PropertySummary,
  scope: { legalEntityId?: string; branchId?: string },
): Promise<string[]> {
  const response = await mediaAssetsService.list(
    {
      propertyId: row.id,
      externalPropertyId: row.externalId ?? undefined,
      legalEntityId: scope.legalEntityId,
      branchId: scope.branchId,
    },
    { page: 0, size: MEDIA_PROBE_PAGE_SIZE, sort: "orderIndex", direction: "asc" },
  );

  if ((response.totalElements ?? 0) === 0) return [];
  return resolvePropertyImageUrls({ mediaAssets: response.content }).length > 0
    ? resolvePropertyImageUrls({ mediaAssets: response.content })
    : propertyHasDisplayableImages(response.content)
      ? []
      : [];
}

export function usePropertyImagePresence(rows: PropertySummary[]) {
  const { legalEntityId, branchId } = useSettingsStore();
  const { profileData } = useProfileStore();
  const [fetchedPresence, setFetchedPresence] = useState<Record<string, boolean>>({});
  const [fetchedImageUrls, setFetchedImageUrls] = useState<Record<string, string[]>>({});
  const [branchIdByPropertyId, setBranchIdByPropertyId] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(false);

  const legalEntities = profileData?.legalEntities ?? [];

  const agencyLogoUrl = useMemo(
    () =>
      resolveAgencyLogoUrl({
        legalEntities,
        branchId,
        legalEntityId,
      }),
    [legalEntities, branchId, legalEntityId],
  );

  const rowKey = useMemo(
    () =>
      rows
        .map((row) => {
          const known = readHasImagesFromRow(row);
          return `${row.id}:${row.externalId ?? ""}:${known === undefined ? "" : known ? "1" : "0"}`;
        })
        .join("|"),
    [rows],
  );

  useEffect(() => {
    if (branchId || rows.length === 0) {
      setBranchIdByPropertyId({});
      return;
    }

    let cancelled = false;

    void (async () => {
      const entries = await Promise.all(
        rows.map(async (row) => {
          const fromRow = readPropertyBranchId(row);
          if (fromRow) {
            cachePropertyBranchId(row.id, fromRow);
            return [row.id, fromRow] as const;
          }

          const cached = getCachedPropertyBranchId(row.id);
          if (cached !== undefined) {
            return [row.id, cached] as const;
          }

          try {
            const detail = await propertiesService.getOne(row.id);
            const resolved = readPropertyBranchId(detail);
            cachePropertyBranchId(row.id, resolved);
            return [row.id, resolved] as const;
          } catch {
            cachePropertyBranchId(row.id, null);
            return [row.id, null] as const;
          }
        }),
      );

      if (!cancelled) {
        setBranchIdByPropertyId(Object.fromEntries(entries));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [branchId, rowKey, rows]);

  useEffect(() => {
    if (rows.length === 0) {
      setFetchedPresence({});
      setFetchedImageUrls({});
      setLoading(false);
      return;
    }

    const scope = {
      legalEntityId: legalEntityId ?? undefined,
      branchId: branchId || undefined,
    };

    // Rows whose image URLs are already embedded in the property summary (CRM-synced thumbnails).
    const directImageUrls = Object.fromEntries(
      rows
        .map((row) => [row.id, resolvePropertyImageUrls(row)] as const)
        .filter((entry): entry is [string, string[]] => entry[1].length > 0),
    );

    // Rows where the server told us the presence status upfront (e.g. hasImages / imageCount).
    const knownPresence = Object.fromEntries(
      rows
        .map((row) => [row.id, readHasImagesFromRow(row)] as const)
        .filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean"),
    );

    // Rows that have direct image URLs already — no DB probe needed.
    const rowsWithDirectUrls = new Set(Object.keys(directImageUrls));

    // Rows whose status is fully known from the summary data — no DB probe needed.
    const rowsWithKnownStatus = new Set(Object.keys(knownPresence));

    // Rows we need to ask the server about.
    const rowsToProbe = rows.filter(
      (row) => !rowsWithDirectUrls.has(row.id) && !rowsWithKnownStatus.has(row.id),
    );

    if (rowsToProbe.length === 0) {
      setFetchedPresence(knownPresence);
      setFetchedImageUrls(directImageUrls);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      // ── Step 1: single batch count query (replaces N individual API calls) ──────
      const propertyIds = rowsToProbe.map((r) => r.id);
      const countMap = await mediaAssetsService.batchCounts(propertyIds);

      if (cancelled) return;

      // Immediately mark zero-asset properties as no-images.
      const batchPresence: Record<string, boolean> = {};
      const rowsNeedingUrls: PropertySummary[] = [];

      for (const row of rowsToProbe) {
        const count = countMap[row.id] ?? 0;
        if (count === 0) {
          batchPresence[row.id] = false;
        } else {
          // Has assets — we need the actual list to resolve displayable image URLs.
          rowsNeedingUrls.push(row);
        }
      }

      // ── Step 2: parallel URL probes only for properties that have assets ────────
      const urlResults = await Promise.all(
        rowsNeedingUrls.map(async (row) => {
          try {
            const urls = await probePropertyImageUrls(row, scope);
            return [row.id, urls] as const;
          } catch {
            return [row.id, [] as string[]] as const;
          }
        }),
      );

      if (cancelled) return;

      const urlPresence: Record<string, boolean> = {};
      const fetchedUrls: Record<string, string[]> = {};
      for (const [id, urls] of urlResults) {
        urlPresence[id] = urls.length > 0;
        if (urls.length > 0) fetchedUrls[id] = urls;
      }

      setFetchedPresence({
        ...knownPresence,
        ...batchPresence,
        ...urlPresence,
      });
      setFetchedImageUrls({
        ...directImageUrls,
        ...fetchedUrls,
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [rowKey, legalEntityId, branchId]);

  const presenceByPropertyId = useMemo(() => {
    const map: Record<string, boolean> = {};

    for (const row of rows) {
      const fromRow = readHasImagesFromRow(row);
      if (typeof fromRow === "boolean") {
        map[row.id] = fromRow;
        continue;
      }
      if (row.id in fetchedPresence) {
        map[row.id] = fetchedPresence[row.id];
      }
    }

    return map;
  }, [rows, fetchedPresence]);

  const imageUrlsByPropertyId = useMemo(() => {
    const map: Record<string, string[]> = {};

    for (const row of rows) {
      const directImageUrls = resolvePropertyImageUrls(row);
      const urls =
        directImageUrls.length > 0
          ? directImageUrls
          : row.id in fetchedImageUrls
            ? fetchedImageUrls[row.id]
            : [];
      map[row.id] = urls;
    }

    return map;
  }, [rows, fetchedImageUrls]);

  const fallbackLogoUrlByPropertyId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of rows) {
      const propertyBranchId = branchId?.trim() || branchIdByPropertyId[row.id] || row.branchId || null;
      map[row.id] = resolveAgencyLogoUrl({
        legalEntities,
        branchId: propertyBranchId,
        legalEntityId: row.legalEntityId ?? legalEntityId,
      });
    }
    return map;
  }, [rows, branchId, branchIdByPropertyId, legalEntities, legalEntityId]);

  return {
    presenceByPropertyId,
    imageUrlsByPropertyId,
    agencyLogoUrl,
    fallbackLogoUrlByPropertyId,
    loading,
  };
}
